import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Truck } from 'lucide-react';

const vazio = {
  cliente_nome: '',
  documento_cliente: '',
  produto_nome: '',
  lote: '',
  quantidade: '',
  unidade: 'Kg',
  data_expedicao: '',
  observacao: ''
};

const ExpedicaoPage = () => {
  const { companyId, getEmpresaId } = useAuth();
  const empresaId = companyId || getEmpresaId?.();
  const [form, setForm] = useState(vazio);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    if (!empresaId || empresaId === 'default') {
      setLoading(false);
      setErro('Empresa não identificada neste usuário.');
      return;
    }
    setLoading(true);
    setErro('');
    try {
      const fn = httpsCallable(functions, 'listExpedicoes');
      const r = await fn({ empresa_id: empresaId });
      setItens(r.data?.data?.items || []);
    } catch (e) {
      setErro(e.message || 'Não foi possível listar as expedições.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  const salvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const fn = httpsCallable(functions, 'createExpedicao');
      await fn({
        empresa_id: empresaId,
        ...form,
        quantidade: Number(String(form.quantidade).replace(',', '.'))
      });
      setForm(vazio);
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao registrar.');
    } finally {
      setSalvando(false);
    }
  };

  const mudarStatus = async (id, status) => {
    try {
      const fn = httpsCallable(functions, 'atualizarStatusExpedicao');
      await fn({ empresa_id: empresaId, id, status });
      await carregar();
    } catch (e) {
      setErro(e.message || 'Erro ao atualizar status.');
    }
  };

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Truck className="h-6 w-6 text-emerald-700" />
          Expedição
        </h1>
        <p className="text-gray-600">Registre a saída do produto acabado e marque quando foi expedido.</p>
      </div>

      {erro && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{erro}</p>}

      <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">
          Cliente
          <input className={`${inputCls} mt-1`} value={form.cliente_nome} onChange={(e) => setForm({ ...form, cliente_nome: e.target.value })} />
        </label>
        <label className="text-sm">
          Documento do cliente
          <input className={`${inputCls} mt-1`} value={form.documento_cliente} onChange={(e) => setForm({ ...form, documento_cliente: e.target.value })} />
        </label>
        <label className="text-sm">
          Produto
          <input className={`${inputCls} mt-1`} value={form.produto_nome} onChange={(e) => setForm({ ...form, produto_nome: e.target.value })} />
        </label>
        <label className="text-sm">
          Lote
          <input className={`${inputCls} mt-1`} value={form.lote} onChange={(e) => setForm({ ...form, lote: e.target.value })} />
        </label>
        <label className="text-sm">
          Quantidade
          <input className={`${inputCls} mt-1`} value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: e.target.value })} />
        </label>
        <label className="text-sm">
          Unidade
          <input className={`${inputCls} mt-1`} value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} />
        </label>
        <label className="text-sm">
          Data
          <input type="date" className={`${inputCls} mt-1`} value={form.data_expedicao} onChange={(e) => setForm({ ...form, data_expedicao: e.target.value })} />
        </label>
        <label className="text-sm md:col-span-2">
          Observação
          <input className={`${inputCls} mt-1`} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </label>
        <div>
          <button
            type="button"
            disabled={salvando}
            onClick={salvar}
            className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            Registrar saída
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b font-medium">Saídas</div>
        {loading ? (
          <p className="p-4 text-sm text-gray-500">Carregando…</p>
        ) : itens.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">Nenhuma expedição registrada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Produto</th>
                <th className="px-3 py-2">Lote</th>
                <th className="px-3 py-2">Qtd</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="px-3 py-2">{item.data_expedicao || '—'}</td>
                  <td className="px-3 py-2">{item.cliente_nome}</td>
                  <td className="px-3 py-2">{item.produto_nome}</td>
                  <td className="px-3 py-2">{item.lote || '—'}</td>
                  <td className="px-3 py-2">{item.quantidade} {item.unidade}</td>
                  <td className="px-3 py-2">
                    <select
                      className="border rounded px-2 py-1"
                      value={item.status}
                      onChange={(e) => mudarStatus(item.id, e.target.value)}
                    >
                      <option value="preparando">Preparando</option>
                      <option value="expedido">Expedido</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExpedicaoPage;
